import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, IconButton, Stack, Table, TableBody, TableCell,
  TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import { Cancel as CancelIcon } from '@mui/icons-material';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getEvent } from '../../api/events';
import { cancelOrder, listOrdersAdmin } from '../../api/orders';
import { useApiCallWithAuth } from '../../hooks/useApiCallWithAuth';
import { CustomerOrderItem } from '../../types/customerOrder';
import { EventItem } from '../../types/event';

type RegistrantStatus = 'Confirmed' | 'Pending' | 'Reserved' | 'Expired' | 'Canceled' | 'Failed';

function registrantStatus(order: CustomerOrderItem): RegistrantStatus {
  if (order.status === 'Paid' || order.status === 'Free') {
    return order.emailConfirmedAt ? 'Confirmed' : 'Pending';
  }
  return order.status;
}

function statusColor(status: RegistrantStatus): 'success' | 'warning' | 'default' | 'error' {
  if (status === 'Confirmed') return 'success';
  if (status === 'Pending' || status === 'Reserved') return 'warning';
  if (status === 'Expired' || status === 'Canceled' || status === 'Failed') return 'error';
  return 'default';
}

export function EventRegistrantsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { request: getEventRequest } = useApiCallWithAuth(getEvent);
  const { request: listOrdersRequest } = useApiCallWithAuth(listOrdersAdmin);
  const { request: cancelOrderRequest } = useApiCallWithAuth(cancelOrder);

  const [event, setEvent] = useState<EventItem | null>(null);
  const [registrants, setRegistrants] = useState<CustomerOrderItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancelTarget, setCancelTarget] = useState<CustomerOrderItem | null>(null);
  const [canceling, setCanceling] = useState(false);

  function loadRegistrants() {
    if (!id) return;
    setLoading(true);
    Promise.all([getEventRequest(id), listOrdersRequest({ eventGuid: id })])
      .then(([eventData, orders]) => { setEvent(eventData); setRegistrants(orders); })
      .catch(() => setError('Failed to load registrants'))
      .finally(() => setLoading(false));
  }

  useEffect(loadRegistrants, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleCancel() {
    if (!cancelTarget) return;
    setCanceling(true);
    try {
      await cancelOrderRequest(cancelTarget.customerOrderGuid);
      setCancelTarget(null);
      loadRegistrants();
    } catch {
      setError('Failed to cancel registration');
    } finally {
      setCanceling(false);
    }
  }

  return (
    <Box sx={{ pt: 3 }}>
      <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ mb: 2 }}>
        <Typography variant='h5' fontWeight={700}>Registrants</Typography>
        <Button variant='outlined' onClick={() => navigate('/events/admin')}>Back to Events</Button>
      </Stack>

      {loading && <CircularProgress size={24} />}
      {error && <Typography color='error' sx={{ mb: 2 }}>{error}</Typography>}

      {!loading && !error && event && (
        <>
          <Stack spacing={0.5} sx={{ mb: 3 }}>
            <Typography variant='h6'>{event.name}</Typography>
            <Typography variant='body2' color='text.secondary'>
              {event.eventDate
                ? new Date(event.eventDate).toLocaleString('en-US', {
                    year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit',
                  })
                : 'No date set'}
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              Capacity: {event.capacity} &middot; Spots left: {event.spotsRemaining}
            </Typography>
          </Stack>

          {registrants.length === 0 ? (
            <Typography color='text.secondary'>No one has registered yet.</Typography>
          ) : (
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Name</strong></TableCell>
                  <TableCell><strong>Email</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell align='right'><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {registrants.map(order => {
                  const status = registrantStatus(order);
                  const canCancel = order.status !== 'Canceled' && order.status !== 'Expired' && order.status !== 'Failed';
                  return (
                    <TableRow key={order.customerOrderGuid} hover>
                      <TableCell><Typography variant='body2'>{order.name ?? '—'}</Typography></TableCell>
                      <TableCell><Typography variant='body2'>{order.email}</Typography></TableCell>
                      <TableCell>
                        <Chip label={status} size='small' color={statusColor(status)} />
                      </TableCell>
                      <TableCell align='right'>
                        {canCancel && (
                          <Tooltip title='Cancel registration'>
                            <IconButton size='small' color='error' onClick={() => setCancelTarget(order)}>
                              <CancelIcon fontSize='small' />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </>
      )}

      <Dialog open={Boolean(cancelTarget)} onClose={() => setCancelTarget(null)}>
        <DialogTitle>Cancel registration?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {cancelTarget?.name ?? cancelTarget?.email} will be marked Canceled and notified by email.
            Their spot will be freed up for others.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelTarget(null)} disabled={canceling}>Keep registration</Button>
          <Button onClick={handleCancel} color='error' variant='contained' disabled={canceling}>
            {canceling ? <CircularProgress size={18} /> : 'Cancel registration'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
